// components/ScrollAffordance.js
//
// "More below" scroll cue for the booking cards. Two coordinated pieces:
//
//   1. A content-mask fade — `mask-image` on the scroll container dissolves
//      the bottom edge of the content into whatever is behind it (the modal's
//      background image on the dark steps, the solid gray on step 3 mobile).
//      Driven purely by the `.has-scroll-cue` class (see style.css).
//
//   2. A bouncing chevron — a button that lives in the (non-scrolling) dialog,
//      NOT inside the scroll container, so the mask doesn't fade it out too.
//      On each recalc we park it over the bottom edge of the scroll viewport
//      via getBoundingClientRect, so it follows whichever card is on screen.
//
// Both appear only while the container is visible, actually overflows, and is
// not already scrolled to the bottom. Visibility self-gates on overflow, so a
// container can be attached even when it isn't the active scroller for the
// current breakpoint (e.g. .nalu-picker scrolls on mobile, the page scrolls on
// desktop) — only the one that truly overflows shows a cue.

const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.5" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';

// How close to the bottom still counts as "at the bottom" (px). Also the
// minimum overflow before we bother showing a cue.
const THRESHOLD = 8;

// Height of the bottom fade, in px. Tall enough to read as a clear "more below"
// cue rather than a thin hairline.
const SCRIM_HEIGHT = 96;

// The fade always resolves to the dark modal tone, on every step — a black
// scrim. (We deliberately don't match each panel's own background; checkout
// would otherwise fade to white and look inconsistent with the other steps.)
const SCRIM_COLOR = 'rgb(8, 8, 11)';

// Every attached container's recalc, plus one shared MutationObserver that
// re-runs them all when a step is shown/hidden. Steps 2-4 are display:none
// when the modal first opens, so they attach while hidden and would otherwise
// only recalc if their ResizeObserver happened to fire on the class toggle.
// Watching the dialog for class/hidden changes makes "became visible" explicit.
const recalcs = new Set();
let navObserver = null;

function ensureNavObserver(dialog) {
  if (navObserver) return;
  let scheduled = false;
  navObserver = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      recalcs.forEach((fn) => fn());
    });
  });
  // class/hidden only — not 'style', so StickySidebar's per-frame transform
  // writes don't spin this up.
  navObserver.observe(dialog, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'hidden'],
  });
}

export function attachScrollAffordance(scrollEl, {
  dialog = scrollEl?.closest('.nalu-booking-dialog'),
  // Also fade the TOP edge once scrolled down. Used by the Step 1 two-column
  // panel, which can overflow in both directions; off elsewhere so the original
  // bottom-only behaviour is unchanged.
  topScrim = false,
  // The bouncing down-chevron cue. Some surfaces (the Step 1 panel) want the
  // fade only, no chevron button.
  showChevron = true,
  // Height of the fade, in px. Defaults to the standard SCRIM_HEIGHT; the Step 1
  // panel passes a shorter fade so it doesn't swallow a whole pax row.
  scrimHeight = SCRIM_HEIGHT,
} = {}) {
  if (!scrollEl || !dialog) return null;

  // Idempotent: re-attaching just forces a recalc (handy after a re-render).
  if (scrollEl._scrollAffordance) {
    scrollEl._scrollAffordance.recalc();
    return scrollEl._scrollAffordance;
  }

  // Bottom fade scrim — a sibling in the (non-scrolling) dialog, parked over
  // the bottom edge of the scroll viewport. Lives outside the scroll container
  // (rather than a mask on it) so it can fade content into the panel's backdrop
  // colour instead of making it transparent over the modal's bg image.
  const scrim = document.createElement('div');
  scrim.className = 'nalu-scroll-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  dialog.appendChild(scrim);

  // Optional top fade scrim — mirror of the bottom one, shown once scrolled
  // away from the top so hidden content above reads as "more up there".
  let scrimTop = null;
  if (topScrim) {
    scrimTop = document.createElement('div');
    scrimTop.className = 'nalu-scroll-scrim';
    scrimTop.setAttribute('aria-hidden', 'true');
    dialog.appendChild(scrimTop);
  }

  let chevron = null;
  if (showChevron) {
    chevron = document.createElement('button');
    chevron.type = 'button';
    chevron.className = 'nalu-scroll-cue';
    chevron.tabIndex = -1;
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = CHEVRON_SVG;
    dialog.appendChild(chevron);

    // Clicking the cue nudges the container down by ~80% of a screen.
    chevron.addEventListener('click', () => {
      scrollEl.scrollBy({ top: Math.round(scrollEl.clientHeight * 0.8), behavior: 'smooth' });
    });
  }

  let raf = 0;
  const recalc = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const ch = scrollEl.clientHeight;
      const sh = scrollEl.scrollHeight;
      const st = scrollEl.scrollTop;

      const visible = ch > 0 && scrollEl.offsetParent !== null;
      const overflowing = sh > ch + THRESHOLD;
      const atBottom = st + ch >= sh - THRESHOLD;
      const atTop = st <= THRESHOLD;
      const show = visible && overflowing && !atBottom;
      const showTop = topScrim && visible && overflowing && !atTop;

      scrim.classList.toggle('is-visible', show);
      if (chevron) chevron.classList.toggle('is-visible', show);
      if (scrimTop) scrimTop.classList.toggle('is-visible', showTop);

      if (show || showTop) {
        // Positions expressed relative to the (positioned) dialog.
        const r = scrollEl.getBoundingClientRect();
        const dr = dialog.getBoundingClientRect();

        if (show) {
          // Scrim spans the container width, sitting just above the bottom edge.
          scrim.style.left = `${r.left - dr.left}px`;
          scrim.style.top = `${r.bottom - dr.top - scrimHeight}px`;
          scrim.style.width = `${r.width}px`;
          scrim.style.height = `${scrimHeight}px`;
          scrim.style.background =
            `linear-gradient(to bottom, transparent, ${SCRIM_COLOR})`;

          if (chevron) {
            // CSS lifts the chevron up off the edge and centers it.
            chevron.style.left = `${r.left - dr.left + r.width / 2}px`;
            chevron.style.top = `${r.bottom - dr.top}px`;
          }
        }

        if (showTop) {
          // Mirror over the top edge, fading upward.
          scrimTop.style.left = `${r.left - dr.left}px`;
          scrimTop.style.top = `${r.top - dr.top}px`;
          scrimTop.style.width = `${r.width}px`;
          scrimTop.style.height = `${scrimHeight}px`;
          scrimTop.style.background =
            `linear-gradient(to top, transparent, ${SCRIM_COLOR})`;
        }
      }
    });
  };

  scrollEl.addEventListener('scroll', recalc, { passive: true });
  window.addEventListener('resize', recalc);
  window.addEventListener('orientationchange', recalc);

  // Catches content re-renders (step 3 rebuilds its body).
  const ro = new ResizeObserver(recalc);
  ro.observe(scrollEl);
  if (scrollEl.firstElementChild) ro.observe(scrollEl.firstElementChild);

  // Re-run when a step is shown/hidden (this is what makes steps 2-4 work,
  // since they attach while display:none and don't reliably trip the RO).
  recalcs.add(recalc);
  ensureNavObserver(dialog);

  const api = {
    recalc,
    destroy() {
      cancelAnimationFrame(raf);
      scrollEl.removeEventListener('scroll', recalc);
      window.removeEventListener('resize', recalc);
      window.removeEventListener('orientationchange', recalc);
      ro.disconnect();
      recalcs.delete(recalc);
      scrim.remove();
      if (scrimTop) scrimTop.remove();
      if (chevron) chevron.remove();
      delete scrollEl._scrollAffordance;
    },
  };

  scrollEl._scrollAffordance = api;
  recalc();
  return api;
}

// Attach the cue to every scrollable booking surface. Safe to call more than
// once — attach is idempotent and unmatched containers are skipped.
const TARGETS = '.nalu-booking-page, .nalu-picker, .nalu-tours-body, #naluFormBodyStep3';

// Exempt steps:
//   #nalu-step-1 — a centered picker that doesn't need a cue, and opening the
//                  passenger dropdown briefly grows content enough to trip it.
//   #nalu-step-3 — checkout; per design, no scroll indicator here.
const EXEMPT = '#nalu-step-1, #nalu-step-3';

export function initScrollAffordances(root = document) {
  root.querySelectorAll(TARGETS).forEach((el) => {
    if (el.closest(EXEMPT)) return;
    attachScrollAffordance(el);
  });
  // Step 1's two-column panel has its own internal scroll area (desktop / iPad
  // landscape). It lives inside #nalu-step-1 (exempt for the page scroller), so
  // attach it explicitly — top + bottom fades, no chevron. On phones/tablet-
  // portrait it's display:contents (no scroll box) and self-gates to nothing.
  root.querySelectorAll('.nalu-picker__scroll').forEach((el) =>
    attachScrollAffordance(el, { topScrim: true, showChevron: false, scrimHeight: SCRIM_HEIGHT / 4 }));
}
